import { Button, Popover } from "antd";
import classNames from "classnames";
import React from "react";
import {
  DeliverableDisplayItem,
  findTaskForStage,
  getLatestTaskStatusLabel,
  getTaskStatusColorClass,
  getTaskStatusLabel,
  OpenTaskDrawerParams,
} from "../../lib";
import styles from "./DeliverableCells.module.scss";

interface DeliverableTitleCellProps {
  item: DeliverableDisplayItem;
  onOpenDeliverable: (deliverableId: string) => void;
  onOpenTaskDrawer?: (params: OpenTaskDrawerParams) => void;
}

export const DeliverableTitleCell: React.FC<DeliverableTitleCellProps> = ({
  item,
  onOpenDeliverable,
  onOpenTaskDrawer,
}) => {
  const parent = item.parentRecord;
  const isCancelled = Boolean(
    parent?.isBrandDeclined ||
      parent?.creatorDeclinedReason ||
      parent?.isCreatorDeclined
  );

  const selectedDeliverable = item.selectedDeliverable;
  const latestTask = selectedDeliverable?.lastestTask;

  const latestStatusLabel = isCancelled
    ? parent?.isBrandDeclined
      ? "Cancelled"
      : "Withdrawn"
    : latestTask
      ? getLatestTaskStatusLabel(
          latestTask.taskName || item.name,
          latestTask.status,
          latestTask.approvalPendingOn
        )
      : "-";

  const latestStatusClass = isCancelled
    ? parent?.isBrandDeclined
      ? styles.taskStatusCancelledCreator
      : styles.taskStatusWithdrawnCreator
    : latestTask
      ? getTaskStatusColorClass(
          styles,
          latestTask.status,
          latestTask.approvalPendingOn
        )
      : "";

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCancelled || !latestTask) return;
    onOpenTaskDrawer?.({
      campaignInfluencerId: parent?._id || "",
      deliverableConfigRefId: item.id,
      taskId: latestTask._id,
      taskType: latestTask.type,
      taskForm: latestTask.form,
    });
  };

  const cancelledReason =
    parent?.brandDeclinedReason || parent?.creatorDeclinedReason;

  return (
    <div className={styles.deliverableTitleWrapper}>
      <button
        type="button"
        className={styles.deliverableNameBtn}
        onClick={(e) => {
          e.stopPropagation();
          onOpenDeliverable(item.id);
        }}
      >
        <span className={styles.deliverableName} title={item.name}>
          {item.name}
        </span>
      </button>
      <div className={styles.deliverableStatusWrapper}>
        {isCancelled ? (
          <Popover content={cancelledReason} trigger="hover" placement="left">
            <span
              className={classNames(
                styles.deliverableStatusChip,
                latestStatusClass,
                styles.nonClickableChip
              )}
              title={latestStatusLabel}
            >
              {latestStatusLabel}
            </span>
          </Popover>
        ) : latestTask ? (
          <span
            className={classNames(
              styles.deliverableStatusChip,
              latestStatusClass,
              styles.clickableChip
            )}
            onClick={handleStatusClick}
            title={latestStatusLabel}
          >
            {latestStatusLabel}
          </span>
        ) : (
          <span className={styles.emptyTaskSmall}>N/A</span>
        )}
      </div>
    </div>
  );
};

interface DeliverableStageCellProps {
  item: DeliverableDisplayItem;
  stageKey: string;
  stageLabel: string;
  onOpenTaskDrawer?: (params: OpenTaskDrawerParams) => void;
}

export const DeliverableStageCell: React.FC<DeliverableStageCellProps> = ({
  item,
  stageKey,
  stageLabel,
  onOpenTaskDrawer,
}) => {
  if (!item.isSaved) {
    return (
      <div className={styles.stageSlot}>
        <span className={styles.emptyTask}>N/A</span>
      </div>
    );
  }

  const parent = item.parentRecord;
  const isCancelled = Boolean(
    parent?.isBrandDeclined ||
      parent?.creatorDeclinedReason ||
      parent?.isCreatorDeclined
  );

  const taskConfig = findTaskForStage(item.tasks, stageKey, stageLabel);

  if (!taskConfig) {
    return (
      <div className={styles.stageSlot}>
        <span className={styles.emptyTask}>N/A</span>
      </div>
    );
  }

  const currentTask = item.selectedDeliverable?.tasks?.find(
    (t) =>
      t._id === taskConfig.taskId ||
      (t.type && t.type.toLowerCase() === stageKey.toLowerCase()) ||
      (t.taskName &&
        (t.taskName.toLowerCase().includes(stageKey.toLowerCase()) ||
          t.taskName.toLowerCase().includes(stageLabel.toLowerCase())))
  );

  const label = isCancelled
    ? parent?.isBrandDeclined
      ? "Cancelled"
      : "Withdrawn"
    : getTaskStatusLabel(currentTask?.status, currentTask?.approvalPendingOn);

  const statusClass = isCancelled
    ? parent?.isBrandDeclined
      ? styles.taskStatusCancelledCreator
      : styles.taskStatusWithdrawnCreator
    : getTaskStatusColorClass(
        styles,
        currentTask?.status,
        currentTask?.approvalPendingOn
      );

  const handleStageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCancelled) return;
    onOpenTaskDrawer?.({
      campaignInfluencerId: parent?._id || "",
      deliverableConfigRefId: item.id,
      taskId: currentTask?._id || taskConfig.taskId,
      taskType: currentTask?.type || taskConfig.type || stageKey,
      taskForm: currentTask?.form,
    });
  };

  const cancelledReason =
    parent?.brandDeclinedReason || parent?.creatorDeclinedReason;

  return (
    <div className={styles.stageSlot}>
      {isCancelled ? (
        <Popover content={cancelledReason} trigger="hover" placement="left">
          <span
            className={classNames(
              styles.stageStatusChip,
              statusClass,
              styles.nonClickableChip
            )}
            title={`${stageLabel}: ${label}`}
          >
            {label}
          </span>
        </Popover>
      ) : (
        <span
          className={classNames(
            styles.stageStatusChip,
            statusClass,
            styles.clickableChip
          )}
          onClick={handleStageClick}
          title={`${stageLabel}: ${label}`}
        >
          {label}
        </span>
      )}
    </div>
  );
};

interface DeliverableViewCellProps {
  onOpenDeliverable: () => void;
}

export const DeliverableViewCell: React.FC<DeliverableViewCellProps> = ({
  onOpenDeliverable,
}) => {
  return (
    <Button
      size="small"
      type="default"
      className={styles.viewButton}
      onClick={(e) => {
        e.stopPropagation();
        onOpenDeliverable();
      }}
    >
      View
    </Button>
  );
};
